import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type { User, UserCreate, UserUpdate, UserListResponse, PaginatedParams } from "@/types";
import type { ChatArchiveListResponse } from "@/types/chat-archive";

interface UserParams extends PaginatedParams {
  isActive?: boolean;
}

export const usersApi = {
  getAll: async (params?: UserParams): Promise<UserListResponse> => {
    const response = await apiClient.get("/users", {
      params: toSnakeCase(params),
    });
    return (toCamelCase(response.data) as UserListResponse) || { items: [], total: 0 };
  },

  getById: async (id: number): Promise<User> => {
    const response = await apiClient.get(`/users/${id}`);
    return toCamelCase(response.data) as User;
  },

  create: async (data: UserCreate): Promise<User> => {
    const response = await apiClient.post("/users", toSnakeCase(data));
    return toCamelCase(response.data) as User;
  },

  update: async (id: number, data: UserUpdate): Promise<User> => {
    const response = await apiClient.put(`/users/${id}`, toSnakeCase(data));
    return toCamelCase(response.data) as User;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  changeOwnPassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    await apiClient.put("/users/me/password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  adminChangePassword: async (
    userId: number,
    newPassword: string
  ): Promise<void> => {
    await apiClient.put(`/users/${userId}/password`, {
      new_password: newPassword,
    });
  },

  // Chat archives (admin only)
  getChatArchives: async (userId: number): Promise<ChatArchiveListResponse> => {
    const response = await apiClient.get(`/users/${userId}/chat-archives`);
    return toCamelCase(response.data) as ChatArchiveListResponse;
  },

  downloadChatArchive: async (userId: number, archiveId: number): Promise<Blob> => {
    const response = await apiClient.get(
      `/users/${userId}/chat-archives/${archiveId}/download`,
      { responseType: "blob" }
    );
    return response.data;
  },

  deleteChatArchive: async (userId: number, archiveId: number): Promise<void> => {
    await apiClient.delete(`/users/${userId}/chat-archives/${archiveId}`);
  },
};
